/**
 * Upload wizard: choose file → confirm columns and units → data checks → save.
 * The file is read and cleaned in the browser; only kept fields are saved.
 */
import {
  ENGINE_VERSION,
  KEPT_FIELDS,
  REQUIRED_FIELDS,
  datasetHash,
  guessMapping,
  guessUnits,
  outwardCode,
  runIntake,
  type ColumnMapping,
  type IntakeResult,
  type KeptField,
  type ServiceMap,
  type UploadUnits,
} from "@ryoma/engine";
import type { Db } from "../data/db.ts";
import { loadTable, type LoadedTable } from "../lib/files.ts";
import { action, date, h, makeDroppable, mount, notice, num, stat, table } from "./dom.ts";

const plural = (n: number, word: string) => `${num(n)} ${word}${n === 1 ? "" : "s"}`;

const LABELS: Record<KeptField, string> = {
  orderId: "Order ID",
  orderDate: "Order date",
  sku: "SKU",
  qty: "Quantity",
  weight: "Parcel weight",
  length: "Length (or one dimensions column)",
  width: "Width",
  height: "Height",
  carrierService: "Carrier service",
  cost: "Shipping cost charged",
  postcode: "Delivery postcode (outward half kept)",
};

interface Ctx {
  db: Db;
  auditId: string;
  serviceCodes: { code: string; label: string }[];
  previousServiceMap: ServiceMap;
  onSaved: () => void;
  onCancel: () => void;
}

export function uploadWizard(ctx: Ctx): HTMLElement {
  const root = h("div", { class: "wizard" });
  let table_: LoadedTable | null = null;
  let mapping: ColumnMapping = {};
  let units: UploadUnits = { weight: "kg", length: "cm", costIncludesVat: false };
  let serviceMap: ServiceMap = { ...ctx.previousServiceMap };
  let result: IntakeResult | null = null;
  let file: File | null = null;

  const stepChoose = () => {
    const input = h("input", { type: "file", accept: ".csv,.xlsx,text/csv", id: "upload-file", class: "file-input" });
    const err = h("div");
    input.addEventListener("change", async () => {
      const f = input.files?.[0];
      if (!f) return;
      file = f;
      mount(err, notice("info", "Reading file on this computer…"));
      try {
        table_ = await loadTable(f);
        mapping = guessMapping(table_.headers);
        units = guessUnits(mapping);
        stepMap();
      } catch (e) {
        mount(err, notice("error", e instanceof Error ? e.message : String(e)));
      }
    });
    mount(root,
      h("h3", null, "Upload client data"),
      h("p", null, "CSV or Excel (.xlsx). The file is read on this computer. Only these fields are saved: ",
        h("strong", null, "order ID, date, SKU, quantity, weight, dimensions, carrier service, cost and the first half of the postcode"),
        ". Names, addresses, emails, phone numbers and every other column never leave this computer."),
      (() => { const zone = h("label", { class: "file-drop", for: "upload-file" }, h("span", null, "Drop a file here, or choose one"), input); makeDroppable(zone, input); return zone; })(),
      err,
      h("div", { class: "row-actions" }, h("button", { type: "button", class: "btn btn-quiet", onClick: ctx.onCancel }, "Cancel")),
    );
  };

  const example = (col: string | undefined, field?: KeptField): string => {
    if (!col || !table_) return "";
    const v = table_.rows.find((r) => String(r[col] ?? "").trim() !== "")?.[col];
    if (v === undefined) return "";
    if (field === "postcode") return `${outwardCode(v) ?? "not recognised"} (kept)`;
    return String(v).slice(0, 40);
  };

  const stepMap = () => {
    if (!table_) return;
    const t = table_;
    const headerOptions = (selected: string | undefined) => [
      h("option", { value: "", selected: !selected }, "— not in file —"),
      ...t.headers.map((hd) => h("option", { value: hd, selected: hd === selected }, hd)),
    ];
    const rows = KEPT_FIELDS.map((f) => {
      const sel = h("select", { "aria-label": LABELS[f], "data-field": f }, headerOptions(mapping[f]));
      const ex = h("span", { class: "example" }, example(mapping[f], f));
      sel.addEventListener("change", () => {
        if (sel.value) mapping[f] = sel.value;
        else delete mapping[f];
        ex.textContent = example(mapping[f], f);
      });
      return [h("span", null, LABELS[f], REQUIRED_FIELDS.includes(f) ? h("span", { class: "req", title: "Required" }, " *") : null), sel, ex];
    });
    const unitSel = (label: string, options: string[], value: string, set: (v: string) => void) => {
      const s = h("select", { "aria-label": label }, options.map((o) => h("option", { value: o, selected: o === value }, o)));
      s.addEventListener("change", () => set(s.value));
      return h("label", { class: "inline-field" }, label, " ", s);
    };
    const vat = h("input", { type: "checkbox", checked: units.costIncludesVat });
    vat.addEventListener("change", () => (units = { ...units, costIncludesVat: vat.checked }));

    let sheetPicker: HTMLElement | null = null;
    if (t.sheetNames.length > 1 && file) {
      const f = file;
      const s = h("select", { "aria-label": "Sheet" }, t.sheetNames.map((n) => h("option", { value: n, selected: n === t.sheet }, n)));
      s.addEventListener("change", async () => {
        table_ = await loadTable(f, s.value);
        mapping = guessMapping(table_.headers);
        units = guessUnits(mapping);
        stepMap();
      });
      sheetPicker = h("label", { class: "inline-field" }, "Sheet ", s);
    }

    mount(root,
      h("h3", null, "Match the columns"),
      h("p", { class: "muted" }, `${t.fileName}: ${num(t.rows.length)} rows, ${t.headers.length} columns. Columns were matched by name; check each one.`),
      sheetPicker ?? "",
      table(["Field", "Column in file", "First value (shown here only)"], rows),
      h("div", { class: "units" },
        unitSel("Weight unit", ["kg", "g", "lb", "oz"], units.weight, (v) => (units = { ...units, weight: v as UploadUnits["weight"] })),
        unitSel("Size unit", ["cm", "mm", "in"], units.length, (v) => (units = { ...units, length: v as UploadUnits["length"] })),
        h("label", { class: "inline-field" }, vat, " Costs include VAT"),
      ),
      h("div", { class: "row-actions" },
        h("button", { type: "button", class: "btn btn-quiet", onClick: stepChoose }, "Choose another file"),
        h("button", { type: "button", class: "btn btn-primary", onClick: () => runChecks() }, "Run data checks"),
      ),
    );
  };

  const runChecks = () => {
    if (!table_) return;
    result = runIntake(table_.rows, mapping, units, serviceMap);
    stepChecked();
  };

  const stepChecked = () => {
    if (!result || !table_) return;
    const r = result;
    if (r.missingFields.length > 0) {
      mount(root,
        h("h3", null, "Data checks"),
        notice("error", "Choose a column for: ", r.missingFields.map((f) => LABELS[f as KeptField]).join(", ")),
        h("div", { class: "row-actions" }, h("button", { type: "button", class: "btn", onClick: stepMap }, "Back to columns")),
      );
      return;
    }
    const s = r.stats;
    const unmapped = r.services.filter((x) => !x.mappedTo);

    const serviceRows = r.services.map((svc) => {
      const sel = h("select", { "aria-label": `Service for ${svc.raw}` },
        h("option", { value: "", selected: !svc.mappedTo }, "— choose —"),
        ctx.serviceCodes.map((c) => h("option", { value: c.code, selected: c.code === svc.mappedTo }, c.label)),
        // Keeps the client's own name, so unpriced services stay distinguishable in results.
        h("option", { value: `UNPRICED:${svc.raw}`, selected: svc.mappedTo?.startsWith("UNPRICED:") }, "Not on any rate card (keep, don't price)"),
      );
      sel.addEventListener("change", () => {
        if (sel.value) serviceMap[svc.raw.toLowerCase()] = sel.value;
        else delete serviceMap[svc.raw.toLowerCase()];
        runChecks();
      });
      return [svc.raw, num(svc.rows), sel];
    });

    const errors = r.issues.filter((i) => i.severity === "error");
    const warnings = r.issues.filter((i) => i.severity === "warning");
    const issueTable = (list: typeof r.issues) =>
      table(["Row", "Order", "Field", "Problem"], list.slice(0, 200).map((i) => [i.row ?? "", i.orderId ?? "", i.field ?? "", i.message]));

    const sample = r.parcels.slice(0, 8).map((p) => [
      p.orderId, date(p.orderDate), p.items.map((i) => `${i.sku}×${i.qty}`).join(", "), num(p.actualWeightKg, 3),
      `${p.dims.lengthCm} × ${p.dims.widthCm} × ${p.dims.heightCm}`, p.carrierService, (p.chargedPence / 100).toFixed(2), p.outwardPostcode ?? "",
    ]);

    const save = action(`Save cleaned data (${num(s.parcels)} parcels)`, async (_btn, status) => {
      if (!file) return;
      if (errors.length > 0 && !confirm(`${errors.length} rows have errors and will be left out. Save the rest?`)) return;
      const hash = await datasetHash(r.parcels);
      await ctx.db.saveUpload(ctx.auditId, {
        file_name: file.name,
        source_rows: s.sourceRows,
        kept_rows: s.keptRows,
        parcel_count: s.parcels,
        date_from: s.dateFrom,
        date_to: s.dateTo,
        error_count: s.errorCount,
        warning_count: s.warningCount,
        mapping,
        units,
        service_map: serviceMap,
        engine_version: ENGINE_VERSION,
        dataset_hash: hash,
      }, r.parcels, r.issues, (done, total) => (status.textContent = `Saving ${num(done)} of ${num(total)}…`));
      status.textContent = "Saved";
      ctx.onSaved();
    }, { primary: true, disabled: s.parcels === 0 || unmapped.length > 0 });

    mount(root,
      h("h3", null, "Data checks"),
      h("div", { class: "stats" },
        stat("Rows in file", num(s.sourceRows)),
        stat("Parcels", num(s.parcels), s.dateFrom ? `${date(s.dateFrom)} to ${date(s.dateTo)}` : undefined),
        stat("Rows left out", num(s.excludedRows + s.duplicateRows), `${plural(s.excludedRows, "error")}, ${plural(s.duplicateRows, "duplicate")}`, s.excludedRows > 0 ? "bad" : undefined),
        stat("Warnings", num(warnings.length), undefined, warnings.length > 0 ? "warn" : undefined),
      ),
      r.issues.some((i) => i.code === "units_suspect") ? notice("warn", "The units look wrong: ", r.issues.filter((i) => i.code === "units_suspect").map((i) => i.message).join("; "), ". Go back and change the unit if so.") : "",
      h("h4", null, "Carrier services"),
      unmapped.length > 0
        ? notice("warn", `Match ${unmapped.length === 1 ? "this service" : `these ${unmapped.length} services`} to a rate card service before saving.`)
        : notice("ok", "Every service is matched."),
      ctx.serviceCodes.length === 0 ? notice("info", "No rate cards yet. Add one under Rate cards & packaging, or mark services as not priced.") : "",
      table(["Service in file", "Rows", "Rate card service"], serviceRows, { numeric: [1] }),
      errors.length > 0 ? [h("h4", null, `Errors: ${num(errors.length)} (rows left out)`), issueTable(errors)] : "",
      warnings.length > 0 ? h("details", null, h("summary", null, `Warnings: ${num(warnings.length)}`), issueTable(warnings)) : "",
      h("h4", null, "What will be saved (first 8 parcels)"),
      table(["Order", "Date", "Items", "Weight kg", "Size cm", "Service", "Cost £", "Postcode"], sample, { numeric: [3, 6] }),
      h("div", { class: "row-actions" },
        h("button", { type: "button", class: "btn btn-quiet", onClick: stepMap }, "Back to columns"),
        save,
      ),
    );
  };

  stepChoose();
  return root;
}
